import { api } from "@/lib/api/client";

export type ZoomRecordingObservationState =
  | "ready"
  | "empty"
  | "delayed"
  | "stale"
  | "refused"
  | "access_denied"
  | "inaccessible"
  | "deleted"
  | "unavailable"
  | "unknown";

export interface ZoomRecordingArtifact {
  provider_recording_id: string;
  file_type: string;
  file_extension: string;
  provider_status: string;
  state?: string;
  recording_start: string | null;
  recording_end: string | null;
  observed_at?: string;
}

export interface ZoomRecordingDestination {
  kind: "provider_admin";
  url: string;
  label: string;
}

export interface ZoomRecordingObservation {
  actor_id: string;
  state: ZoomRecordingObservationState;
  lifecycle_state?: string;
  complete: boolean;
  observed_at: string;
  last_successful_at: string | null;
  artifacts?: ZoomRecordingArtifact[];
  destination?: ZoomRecordingDestination;
  data?: {
    artifacts: ZoomRecordingArtifact[];
    destination: ZoomRecordingDestination;
  } | null;
}

export interface ZoomRecordingDiagnostic {
  provider_code: string | null;
  recovery_action: string | null;
}

export interface ZoomRecordingResponse {
  capability_id: "zoom.recordings";
  observation: ZoomRecordingObservation;
  artifact_count?: number;
  diagnostic?: ZoomRecordingDiagnostic;
  recovery?: {
    action: "refresh_exact_session";
    detail: string;
  };
}

export function isZoomManagementDestination(
  destination: ZoomRecordingDestination | undefined,
): destination is ZoomRecordingDestination {
  if (!destination || destination.kind !== "provider_admin") return false;
  try {
    const url = new URL(destination.url);
    return (
      url.protocol === "https:"
      && url.hostname === "zoom.us"
      && url.port === ""
      && url.pathname === "/recording/management"
      && url.search === ""
      && url.hash === ""
    );
  } catch {
    return false;
  }
}

export function zoomRecordingQueryKey(sessionId: string): readonly unknown[] {
  return ["zoom-recordings", sessionId];
}

export const zoomRecordingsApi = {
  get: (sessionId: string) =>
    api.get<ZoomRecordingResponse>(`/sessions/${sessionId}/zoom/recordings`),
  refresh: (sessionId: string) =>
    api.post<ZoomRecordingResponse>(`/sessions/${sessionId}/zoom/recordings/refresh`, {}),
};
